import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import useProjectService, { Project } from '../../../services/ProjectService';

/**
 * Project Timeline Page
 * Shows timeline view of project milestones and progress
 */
const ProjectTimeline: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { getProjects } = useProjectService();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projectsResult = await getProjects({ per_page: 10 });
        setProjects(projectsResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [getProjects]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon color="success" />;
      case 'active': return <PlayArrowIcon color="primary" />;
      case 'on_hold': return <PauseIcon color="warning" />;
      default: return <ScheduleIcon color="action" />;
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'on_hold': return 'warning';
      case 'cancelled': return 'error';
      case 'planning': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading project timeline...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (projects.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Projects Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You don't have any projects to display in the timeline.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        {projects.map((project, index) => (
          <Box key={project.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Timeline indicator */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: getStatusColor(project.status) === 'success' ? 'success.main' : 'primary.main' }}>
                {getStatusIcon(project.status)}
              </Avatar>
              {index < projects.length - 1 && (
                <Box 
                  sx={{ 
                    width: 2, 
                    height: 60, 
                    bgcolor: 'divider', 
                    mt: 1 
                  }} 
                />
              )}
            </Box>

            {/* Project card */}
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography variant="h6" component="h3">
                    {project.name}
                  </Typography>
                  <Chip 
                    label={project.status} 
                    color={getStatusColor(project.status)}
                    size="small"
                  />
                </Stack>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {project.description}
                </Typography>

                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(project.created_at).toLocaleDateString()}
                  </Typography>
                  
                  {project.expected_start_date && (
                    <Typography variant="caption" color="text.secondary">
                      Expected Start: {new Date(project.expected_start_date).toLocaleDateString()}
                    </Typography>
                  )}
                  
                  {project.actual_start_date && (
                    <Typography variant="caption" color="text.secondary">
                      Actual Start: {new Date(project.actual_start_date).toLocaleDateString()}
                    </Typography>
                  )}
                  
                  {project.expected_end_date && (
                    <Typography variant="caption" color="text.secondary">
                      Expected End: {new Date(project.expected_end_date).toLocaleDateString()}
                    </Typography>
                  )}
                  
                  {project.actual_end_date && (
                    <Typography variant="caption" color="text.secondary">
                      Actual End: {new Date(project.actual_end_date).toLocaleDateString()}
                    </Typography>
                  )}
                </Stack>

                {/* Progress bar */}
                {project.completion_percentage !== undefined && (
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption">Progress</Typography>
                      <Typography variant="caption">{project.completion_percentage}%</Typography>
                    </Stack>
                    <LinearProgress 
                      variant="determinate" 
                      value={project.completion_percentage} 
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ProjectTimeline;